/**
 * RetrieveTagsTable.
 * (2026-09-28, Jason Cox) The scanned-tag table for the Retrieve Tags page.
 * Shows each tag's product, smartbox, quantity and whether it can be
 * retrieved -- with a plain-language reason when it can't.
 */
import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { RetrieveBlockReason, RetrieveTagCheck } from '../services/smart/retrieveApi';

/**
 * Where a scanned tag is in the retrieve flow:
 * - checking:    Smart check in flight
 * - ready:       passed the check, will be flagged on submit
 * - blocked:     failed the check (see check.reason)
 * - flagged:     flagged and confirmed by a re-check after submit
 * - unconfirmed: submitted, but the re-check did not show it flagged
 */
export type RetrieveRowState = 'checking' | 'ready' | 'blocked' | 'flagged' | 'unconfirmed';

export interface RetrieveRow {
  rfid: string;
  state: RetrieveRowState;
  check?: RetrieveTagCheck;
}

/**
 * Translate a block reason into wording a vendor at the box will understand.
 *
 * @param reason - why the Smart check rejected the tag
 * @param message - server message, used for unexpected errors
 * @returns short explanation for the Status column
 */
export function explainBlockReason(reason?: RetrieveBlockReason, message?: string): string {
  switch (reason) {
    case 'already_flagged':
      return 'Already flagged for retrieval';
    case 'consumed':
      return 'Already consumed, cannot be retrieved';
    case 'not_yours':
      return 'Belongs to another vendor';
    case 'not_found':
      return 'Not a recognized tag';
    case 'not_in_smartbox':
      return 'Not in a SmartBox';
    case 'error':
      return `Check failed${message ? `: ${message}` : ''}`;
    default:
      return 'Cannot be retrieved';
  }
}

const STATE_BADGE: Record<RetrieveRowState, { label: string; className: string }> = {
  checking: { label: 'Checking...', className: 'bg-secondary-100 text-secondary-700' },
  ready: { label: 'Ready', className: 'bg-green-100 text-green-800' },
  blocked: { label: '', className: 'bg-red-100 text-red-800' },
  flagged: { label: 'Flagged: remove from box', className: 'bg-primary-100 text-primary-800' },
  unconfirmed: { label: 'Not confirmed: check again', className: 'bg-yellow-100 text-yellow-800' },
};

interface RetrieveTagsTableProps {
  rows: RetrieveRow[];
  /** Omit to hide the remove buttons (e.g. after submit). */
  onRemove?: (rfid: string) => void;
}

const RetrieveTagsTable: React.FC<RetrieveTagsTableProps> = ({ rows, onRemove }) => (
  <div className="card overflow-x-auto">
    <table className="min-w-full divide-y divide-secondary-200 text-sm">
      <thead className="bg-secondary-50">
        <tr>
          <th className="px-4 py-2 text-left font-medium text-secondary-700">EPC / RFID</th>
          <th className="px-4 py-2 text-left font-medium text-secondary-700">Item No.</th>
          <th className="px-4 py-2 text-left font-medium text-secondary-700">Description</th>
          <th className="px-4 py-2 text-left font-medium text-secondary-700">SmartBox</th>
          <th className="px-4 py-2 text-right font-medium text-secondary-700">Qty</th>
          <th className="px-4 py-2 text-center font-medium text-secondary-700">Status</th>
          {onRemove && <th className="px-4 py-2"></th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-secondary-100">
        {rows.length === 0 && (
          <tr>
            <td colSpan={onRemove ? 7 : 6} className="px-4 py-6 text-center text-secondary-500">
              No tags scanned yet. Scan the items you are taking out of the SmartBox.
            </td>
          </tr>
        )}
        {rows.map((row) => {
          const badge = STATE_BADGE[row.state];
          const label =
            row.state === 'blocked'
              ? explainBlockReason(row.check?.reason, row.check?.message)
              : badge.label;
          return (
            <tr key={row.rfid}>
              <td className="px-4 py-2 font-mono">{row.rfid}</td>
              <td className="px-4 py-2">{row.check?.sku || ''}</td>
              <td className="px-4 py-2">{row.check?.itemName || ''}</td>
              <td className="px-4 py-2">{row.check?.smartboxName || ''}</td>
              <td className="px-4 py-2 text-right">{row.check?.packageQty ?? ''}</td>
              <td className="px-4 py-2 text-center">
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${badge.className}`}>
                  {label}
                </span>
              </td>
              {onRemove && (
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => onRemove(row.rfid)}
                    className="text-red-500 hover:text-red-700"
                    aria-label={`Remove ${row.rfid}`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default RetrieveTagsTable;
